/**
 * Axios instance with interceptors for authentication
 * Reason: Centralize axios configuration and handle auth tokens automatically
 */

import axios from 'axios';
import { toast } from 'sonner';
import { OPEN_CREDITS_EVENT } from '../tiptap/lib/credits';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple redirects
let isRedirecting = false;

// Aplana error.response.data.detail a string: FastAPI 422 lo devuelve como ARRAY
// de objetos {type, loc, msg, ...}; renderizarlo directo revienta React (#31).
function normalizeErrorDetail(error) {
  const d = error?.response?.data?.detail;
  if (d == null || typeof d === 'string') return;
  try {
    if (Array.isArray(d)) {
      error.response.data.detail = d
        .map((e) => e?.msg || (typeof e === 'string' ? e : JSON.stringify(e)))
        .join(' · ');
    } else if (typeof d === 'object') {
      error.response.data.detail = d.msg || d.message || JSON.stringify(d);
    } else {
      error.response.data.detail = String(d);
    }
  } catch {
    error.response.data.detail = 'Error en la solicitud';
  }
}

// Request interceptor - Add auth token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    normalizeErrorDetail(error);
    if (error.response?.status === 401) {
      // Token expired or invalid
      console.error('401 Unauthorized - Token expired or invalid');

      // Clear auth data immediately
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Only redirect once
      if (!isRedirecting && !window.location.pathname.includes('/auth')) {
        isRedirecting = true;
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');

        // Force redirect to login
        setTimeout(() => {
          window.location.href = '/auth';
          isRedirecting = false;
        }, 100);
      }
    } else if (error.response?.status === 402) {
      // Sin créditos: el bloqueo se convierte en una INVITACIÓN a comprar. Única
      // fuente de verdad — los call sites miran error.__creditsHandled para no
      // sacar un segundo toast.
      // Nota: hoy el 402 SOLO nace de `enforce_credits` (backend
      // `services/credits.py`), llamado únicamente desde `ai_service.py`; esas
      // llamadas ocurren en el editor, donde CreditsPanel está montado y escucha
      // OPEN_CREDITS_EVENT. Si algún día una pantalla SIN CreditsPanel puede
      // recibir un 402, el botón no haría nada: montar el panel ahí o navegar
      // a /planes.
      error.__creditsHandled = true;
      const detail = error.response?.data?.detail
        || 'No tienes créditos suficientes. Compra un paquete de créditos.';
      toast.error(detail, {
        id: 'credits-402',
        duration: 8000,
        action: {
          label: 'Comprar créditos',
          onClick: () => window.dispatchEvent(new Event(OPEN_CREDITS_EVENT)),
        },
      });
    } else if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
      toast.error('No tienes permisos para realizar esta acción.');
    } else if (error.response?.status >= 500) {
      // Server error
      toast.error('Error del servidor. Por favor, intenta más tarde.');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

